import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { AdminStats } from '@/lib/types'
import { apiClient, TokenExpiredError } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'
import { authService } from '@/lib/auth'

export function AdminPage() {
  const [stats, setStats] = useState<AdminStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()
  const navigate = useNavigate()

  const loadStats = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await apiClient.getAdminStats()
      setStats(data)
    } catch (error) {
      // Handle token expiration - redirect to login page
      if (error instanceof TokenExpiredError) {
        await authService.logout()
        navigate('/login?expired=true', { replace: true })
        return
      }
      
      console.error('Failed to load admin stats:', error)
      toast({
        title: "Error",
        description: "Failed to load admin statistics",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast, navigate])

  useEffect(() => {
    loadStats()
  }, [loadStats])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="spinner w-8 h-8"></div>
          <p className="text-muted-foreground">Loading admin data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <Button onClick={loadStats} variant="outline">
              Refresh
            </Button>
          </div>

          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-sm font-medium text-muted-foreground">Total Users</h3>
                <p className="text-2xl font-bold">{stats.total_users}</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-sm font-medium text-muted-foreground">Total Conversations</h3>
                <p className="text-2xl font-bold">{stats.total_conversations}</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-sm font-medium text-muted-foreground">Total Messages</h3>
                <p className="text-2xl font-bold">{stats.total_messages}</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="text-sm font-medium text-muted-foreground">Active Today</h3>
                <p className="text-2xl font-bold">{stats.active_conversations_today}</p>
              </div>
            </div>
          )}

          <div className="bg-card p-6 rounded-lg border">
            <h2 className="text-xl font-semibold mb-4">System Information</h2>
            <div className="space-y-2 text-sm">
              <p><span className="font-medium">Requests per minute:</span> {stats?.requests_per_minute.toFixed(2) || 'N/A'}</p>
              <p><span className="font-medium">Average response time:</span> {stats?.average_response_time.toFixed(2) || 'N/A'}ms</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

